import PingAnimation from "components/PingAnimation/PingAnimation";
import { orderStatusConstants } from "constants/order";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState, useRef, useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useUpdateDefaultAccountMutation } from "store/queries/users";
import { onStaffAccountChange } from "store/slices/user";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";

const StyledDropdown = styled(AutoComplete)<{ footerVisible: boolean }>`
  min-width: 330px !important;
  .p-autocomplete-items {
    position: relative !important;
    padding-bottom: ${({ footerVisible }) =>
      footerVisible ? "0px" : "10px"} !important;
    li {
      padding-top: 0px !important;
      padding-bottom: 0px !important;
      min-height: 52px !important;
      padding-left: 0px !important;
      &:hover {
        background-color: white !important;
      }
    }
    li:last-child {
      padding-bottom: ${({ footerVisible }) =>
        footerVisible ? "12px" : "0px"} !important;
    }
    .name-container {
      padding-top: 16px !important;
      padding-bottom: 16px !important;
      padding-left: 20px !important;
      flex: 1 1 auto;
      &:hover {
        background-color: var(--surface-100) !important;
      }
    }
  }
  ${({ footerVisible }) =>
    footerVisible &&
    `
      .p-autocomplete-items > li:last-child {
        position: sticky !important;
        bottom: 0 !important;
        background: white !important;
      }
    `}
`;

const StyledInputSwitch = styled(InputSwitch)`
  width: 32px;
  height: 18px;

  .p-inputswitch-slider {
    border-radius: 12px;
  }

  .p-inputswitch-slider:before {
    width: 14px;
    height: 14px;
    top: 11px;
    left: 2px;
  }

  &.p-inputswitch-checked .p-inputswitch-slider:before {
    transform: translateX(12px);
  }
`;

function ChangeAccountDropdown({
  defaultSelected,
  accounts,
  onAccountChange,
  userDefaultAccount,
  userId,
  onUserRefetch,
}: {
  defaultSelected: string | null;
  accounts: IAccount[];
  onAccountChange?: (account: IAccount) => void;
  userDefaultAccount?: string | null;
  userId?: string | null;
  onUserRefetch?: () => void;
}) {
  const acRef = useRef<AutoComplete | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const [updateDefaultAccount] = useUpdateDefaultAccountMutation();
  const { current: toastElement } = useContext(ToastContext);

  const [selectedAccount, setSelectedAccount] = useState<string | null>(
    defaultSelected
  );
  const [filteredAccounts, setFilteredAccounts] = useState<IAccount[]>([]);
  const [defaultAccount, setDefaultAccount] = useState<
    string | null | undefined
  >(userDefaultAccount);
  const [isDefaultUpdating, setIsDefaultUpdating] = useState(false);

  const { setIsLoading } = useLoading();
  const navigate = useNavigate();

  const parsedFilteredAccounts = useMemo(() => {
    const accountsList = filteredAccounts.map((account: IAccount) => {
      const pendingOrdersCounts = account.orders.filter(
        (order) => order.status === orderStatusConstants.DRAFT
      ).length;
      return {
        name: account.name,
        pending: pendingOrdersCounts,
        id: account.id,
      };
    });
    const shouldShowFooter =
      !!defaultAccount && defaultAccount !== userDefaultAccount;
    if (shouldShowFooter) {
      accountsList.push({ type: "footer" } as any);
    }
    return accountsList;
  }, [filteredAccounts, defaultAccount, userDefaultAccount]);

  const searchAccount = (event: { query: string }) => {
    setTimeout(() => {
      let filteredAccountsInternal;
      if (!event.query.trim().length) {
        filteredAccountsInternal = [...accounts];
      } else {
        filteredAccountsInternal = accounts.filter((account: IAccount) => {
          return account.name
            .toLowerCase()
            .startsWith(event.query.toLowerCase());
        });
      }
      setFilteredAccounts(filteredAccountsInternal);
    }, 250);
  };

  const onAutoCompleteChange = (item: any) => {
    setSelectedAccount(item.name);
    const newlySelectedAccount = accounts.find(
      (theAccount) => theAccount.name === item.name
    );
    if (newlySelectedAccount?.id) {
      onAccountChange?.(newlySelectedAccount);
      dispatch(onStaffAccountChange({ currentAccount: newlySelectedAccount }));
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        if (location.pathname === "/orders") {
          navigate(`/orders?accountId=${newlySelectedAccount?.id}`);
        } else {
          navigate(`/hospitals/view/${newlySelectedAccount?.id}`);
        }
      }, 1000);
    }
    acRef.current?.hide();
  };

  const handleChangeDefaultAccount = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (userId && defaultAccount) {
      setIsDefaultUpdating(true);
      try {
        await updateDefaultAccount({
          userId,
          defaultAccount,
        }).unwrap();
        setIsDefaultUpdating(false);
        acRef.current?.hide();
        toastElement?.show({
          severity: "success",
          summary: "Success",
          detail: "Default hospital updated successfully",
        });
        // Refetch user data to update the component with new default
        onUserRefetch?.();
      } catch (error) {
        console.error(error);
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to update default hospital",
        });
      }
    }
  };

  const itemTemplate = (item: any) => {
    if ("type" in item && item.type === "footer") {
      return (
        <div className="pt-2 border-top-1 surface-border account-ac-footer flex justify-content-end align-items-center">
          <Button
            label="Change default hospital"
            className="p-button-sm"
            disabled={!userId || !defaultAccount}
            loading={isDefaultUpdating}
            onClick={(e) => handleChangeDefaultAccount(e)}
          />
        </div>
      );
    }
    return (
      <div
        role="presentation"
        className="flex gap-2 justify-content-between align-items-center"
        onMouseDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <button
          type="button"
          className={`flex gap-2 align-items-center bg-transparent border-none p-0 cursor-pointer name-container ${
            item.name === selectedAccount ? "bg-gray-200" : ""
          }`}
          onClick={() => onAutoCompleteChange(item)}
        >
          {item.name}
          {item.pending ? <PingAnimation count={item.pending} /> : null}
        </button>
        <Tooltip
          target=".default-account-icon"
          style={{
            fontSize: "13px",
            width: "200px",
          }}
        />
        <StyledInputSwitch
          className="default-account-icon"
          data-pr-tooltip="Set as default hospital"
          checked={item.id === defaultAccount}
          onChange={(e) => {
            setDefaultAccount(item.id);
          }}
        />
      </div>
    );
  };

  return (
    <StyledDropdown
      dropdown
      appendTo="self"
      value={selectedAccount}
      footerVisible={!!defaultAccount && defaultAccount !== userDefaultAccount}
      completeMethod={searchAccount}
      placeholder="Ex: Hospital Clear"
      suggestions={parsedFilteredAccounts}
      onChange={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      itemTemplate={itemTemplate}
      panelClassName="account-ac-panel"
      ref={acRef}
    />
  );
}

export default ChangeAccountDropdown;
